import React, { useState } from "react";
import { Box, Typography, Collapse } from "@mui/material";
import { COLORS, cardSx } from "./wtStyle";

// Site Visit verification status — informational only. Every Site Visit
// bucket lead's userParentId now changes to the recipient's manager
// regardless of status (the earlier "approved/rejected leave it untouched"
// rule was removed).
const VERIFICATION_LABELS = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
};

const formatDate = (ms) => {
  if (!ms) return "—";
  return new Date(ms).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function DetailField({ label, value }) {
  return (
    <Box sx={{ minWidth: 130 }}>
      <Typography fontSize={10.5} color={COLORS.textSecondary} sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Typography fontSize={12.5} fontWeight={600}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

// Recognized bucket names that already tell us which extra fields apply.
// Callers with no bucket context (e.g. Transfer History's batch detail view,
// which shows leads that moved through any bucket, or none at all) pass
// bucketName={null} instead — in that case we fall back to detecting from
// whatever fields are actually present on the lead, since a WorkTransferLogs
// row's entityType alone doesn't say which bucket it came from.
const KNOWN_BUCKET_NAMES = new Set(["Token", "Allotment", "SiteVisitVerified", "SiteVisitUnverified", "SiteVisitRejected"]);

export function LeadRow({ lead, bucketName, defaultExpanded }) {
  const [expanded, setExpanded] = useState(Boolean(defaultExpanded));
  const isCp = lead.leadtype === "CP";
  const isKnownBucket = KNOWN_BUCKET_NAMES.has(bucketName);
  const isToken = isKnownBucket ? bucketName === "Token" : Boolean(lead.tokenname || lead.eventname);
  const isAllotment = isKnownBucket ? bucketName === "Allotment" : Boolean(lead.wingname || lead.unitno);
  // Owner-side Site Visit bucket (Leads.userId) — 3 sub-buckets by the lead's
  // own verification status. Distinct from the approver-side "Site visit
  // approval pending with you" section (Leads.userParentId), which is always
  // pending by construction (see getPendingSiteVisitApprovals) so it doesn't
  // need its own Verification field here.
  const isSiteVisit = isKnownBucket
    ? bucketName === "SiteVisitVerified" || bucketName === "SiteVisitUnverified" || bucketName === "SiteVisitRejected"
    : Boolean(lead.siteVisitVerificationStatus);

  return (
    <Box sx={{ borderTop: `1px solid ${COLORS.border}` }}>
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          py: 1,
          px: 1.5,
          cursor: "pointer",
          "&:hover": { bgcolor: COLORS.sunken },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography fontSize={13.5} fontWeight={600} noWrap>
            {lead.leadname || "Unnamed lead"}
          </Typography>
          <Typography fontSize={12} color={COLORS.textSecondary} noWrap>
            {lead.salesname || "—"}
          </Typography>
        </Box>
        {lead.leadstatus && (
          <Typography
            fontSize={11.5}
            fontWeight={600}
            noWrap
            sx={{ px: 1, py: 0.3, borderRadius: 1, bgcolor: COLORS.sunken, color: COLORS.textSecondary, flexShrink: 0 }}
          >
            {lead.leadstatus}
          </Typography>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          <Typography fontSize={12} sx={{ fontFamily: "monospace", color: COLORS.textSecondary }}>
            {lead.leaddisplayid || lead.id}
          </Typography>
          <Typography fontSize={12} color={COLORS.blue} sx={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform .15s" }}>
            ▾
          </Typography>
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, px: 1.5, pb: 1.5, bgcolor: COLORS.sunken }}>
          {isToken ? (
            <>
              <DetailField label="Token" value={lead.tokenname} />
              <DetailField label="Event" value={lead.eventname} />
            </>
          ) : (
            <>
              <DetailField label="Project" value={lead.projectshortcode} />
              <DetailField label="Unit type" value={lead.unittype} />
              {isAllotment && <DetailField label="Wing" value={lead.wingname} />}
              {isAllotment && <DetailField label="Unit no" value={lead.unitno} />}
            </>
          )}
          {isSiteVisit && (
            <DetailField
              label="Verification"
              value={VERIFICATION_LABELS[lead.siteVisitVerificationStatus] || lead.siteVisitVerificationStatus}
            />
          )}
          <DetailField label="Lead type" value={lead.leadtype} />
          <DetailField label="Created at" value={formatDate(lead.created_at)} />
          {isCp && <DetailField label="CP" value={lead.cpname} />}
          {isCp && <DetailField label="FOS" value={lead.fosname} />}
        </Box>
      </Collapse>
    </Box>
  );
}

// Side panel that shows the individual leads for whichever bucket the user
// clicked "View leads" on — sits in the page's right-hand column instead of
// pushing the bucket list around inline. Each lead row is its own dropdown —
// collapsed shows name/sales-manager/CUID, expanded reveals project (short
// code), unit type, lead type, created date, and CP/FOS when the lead's
// source is a CP.
export default function LeadDetailsPanel({ bucketName, leads }) {
  if (!bucketName) {
    return (
      <Box sx={{ ...cardSx, flex: 1, minHeight: 240, p: 4, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography fontSize={13.5} color={COLORS.textSecondary} sx={{ textAlign: "center" }}>
          Click "View leads" on a bucket to see individual leads here.
        </Typography>
      </Box>
    );
  }

  const APPROVAL_DISPLAY_NAMES = {
    CancellationApproval: "Cancellation approval",
    UnitShiftApproval: "Unit shift approval",
    SiteVisitApproval: "Site visit approval pending with you",
    SiteVisitVerified: "Site Visit — Verified",
    SiteVisitUnverified: "Site Visit — Unverified",
    SiteVisitRejected: "Site Visit — Rejected",
  };
  const displayName = APPROVAL_DISPLAY_NAMES[bucketName] || bucketName;

  return (
    <Box sx={{ ...cardSx, flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${COLORS.border}`, bgcolor: COLORS.sunken, flexShrink: 0 }}>
        <Typography fontSize={13} fontWeight={700}>
          {displayName} — {leads.length} lead{leads.length === 1 ? "" : "s"}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {leads.map((lead) => (
          <LeadRow key={lead.id} lead={lead} bucketName={bucketName} />
        ))}
      </Box>
    </Box>
  );
}
